import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
  Tooltip,
  IconButton,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import TagEditor from '../Widgets/TagEditor';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return ['1', 'true', 'yes', 'on'].includes(normalized);
};

const formatPlanTimestamp = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const ActionsDeterministicAnswer = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  actionType = 'deterministicanswer',
  actionTitle,
  defaultOutputTag = 'deterministic_result',
  defaultOutputDescription,
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [outputTag, setOutputTag] = useState(defaultOutputTag);
  const [outputDescription, setOutputDescription] = useState(
    defaultOutputDescription || t('deterministicanswer.result_description', 'Deterministic answer result')
  );
  const [freezePlan, setFreezePlan] = useState(false);
  const [clearCompiledPlanRequested, setClearCompiledPlanRequested] = useState(false);
  const [compiledPlanInfo, setCompiledPlanInfo] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const promptRef = useRef(null);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    handleConditionsChange,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState,
  } = useActionSettings({
    settingId,
    caseid,
    actionType,
    onSaveStatusChange,
    customComparison: (initial) => {
      if (!initial) return false;
      return (
        prompt === (initial.prompt || '') &&
        outputTag === (initial.outputTag || defaultOutputTag) &&
        outputDescription === (initial.outputDescription || (defaultOutputDescription || t('deterministicanswer.result_description', 'Deterministic answer result'))) &&
        freezePlan === parseBoolean(initial.freezePlan) &&
        clearCompiledPlanRequested === parseBoolean(initial.clearCompiledPlan)
      );
    },
    customSaveData: () => ({
      prompt,
      outputTag,
      outputDescription,
      freezePlan,
      ...(clearCompiledPlanRequested ? { clearCompiledPlan: true } : {}),
      useConditions: true,
      conditions,
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      if (customData.prompt !== undefined) {
        setPrompt(customData.prompt || '');
      } else if (customData.instructions !== undefined) {
        setPrompt(customData.instructions || '');
      } else if (customData.goal !== undefined) {
        setPrompt(customData.goal || '');
      } else {
        setPrompt('');
      }

      if (customData.outputTag !== undefined) {
        setOutputTag(customData.outputTag || defaultOutputTag);
      } else {
        setOutputTag(defaultOutputTag);
      }

      if (customData.outputDescription !== undefined) {
        setOutputDescription(
          customData.outputDescription || (defaultOutputDescription || t('deterministicanswer.result_description', 'Deterministic answer result'))
        );
      } else {
        setOutputDescription(defaultOutputDescription || t('deterministicanswer.result_description', 'Deterministic answer result'));
      }

      setFreezePlan(parseBoolean(customData.freezePlan));
      setClearCompiledPlanRequested(false);
      setCompiledPlanInfo({
        plan: customData.compiledPlan || null,
        generatedAt: customData.compiledPlanGeneratedAt || '',
        promptHash: customData.compiledPlanPromptHash || '',
      });
    },
  });

  useEffect(() => {
    if (updateSaveDisabledState) {
      updateSaveDisabledState();
    }
  }, [prompt, outputTag, outputDescription, freezePlan, clearCompiledPlanRequested, updateSaveDisabledState]);

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType('');
  }, []);

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'prompt') {
      promptRef.current?.insertTag(value);
    } else if (menuType === 'outputTag') {
      setOutputTag(value);
    }
    handleMenuClose();
  }, [handleMenuClose, menuType]);

  const resolvedActionTitle =
    actionTitle || t('deterministicanswer', 'Deterministic Answer');
  const compiledPlan = compiledPlanInfo?.plan || null;
  const compiledPlanStepCount = Array.isArray(compiledPlan?.steps) ? compiledPlan.steps.length : 0;
  const compiledPlanSourceMode = compiledPlan?.source_mode || compiledPlan?.mode || '';
  const compiledPlanGeneratedAtLabel = formatPlanTimestamp(compiledPlanInfo?.generatedAt);
  const hasCompiledPlan = Boolean(compiledPlan);
  const compiledPlanJson = hasCompiledPlan ? JSON.stringify(compiledPlan, null, 2) : '';

  return (
    <ActionSettingsLayout
      title={resolvedActionTitle}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      tagsDropdownProps={{
        tags,
        handleTagSelect,
        anchorEl,
        setAnchorEl: handleMenuClose,
        menuType,
      }}
    >
      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          title={(
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                <AutoFixHighIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                1
              </Box>
              {t('deterministicanswer.prompt_title', 'Deterministic Prompt')}
            </Typography>
          )}
          action={(
            <Tooltip title={t('deterministicanswer.prompt_help_tooltip', 'This prompt is compiled into a reusable per-event execution plan. You can freeze the compiled plan after the first run.')}>
              <IconButton size="small" sx={{ color: '#5757b0' }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
              {t('deterministicanswer.prompt_label', 'Prompt')}
            </Typography>
            <Box sx={{
              border: '1px solid #d0d0e8',
              borderRadius: '8px',
              backgroundColor: '#fff',
              '&:hover': {
                borderColor: '#5757b0',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
              },
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            }}>
              <TagEditor
                caseid={caseid}
                ref={promptRef}
                value={prompt}
                onChange={setPrompt}
                multiline
                rows={10}
                placeholder={t(
                  'deterministicanswer.prompt_placeholder',
                  'Describe the transformation you want. Example: extract the weekly date and count, then return a normalized JSON row.'
                )}
                onTagClick={(event) => handleMenuOpen(event, 'prompt')}
                tags={tags}
              />
            </Box>
            <Typography sx={{ mt: 1, fontSize: '0.85rem', color: '#666' }}>
              {t(
                'deterministicanswer.prompt_help',
                'The backend will compile this prompt into a reusable deterministic plan and persist it on first run. Freeze it to keep the compiled plan fixed across later runs.'
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #5757b010',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          title={(
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Box component="span" sx={{
                bgcolor: '#5757b0',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
                boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
              }}>
                <LocalOfferIcon sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                2
              </Box>
              {t('deterministicanswer.output_title', 'Output Tag')}
            </Typography>
          )}
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
              {t('deterministicanswer.output_tag_label', 'Output Tag')}
            </Typography>
            <Box sx={{
              border: '1px solid #d0d0e8',
              borderRadius: '8px',
              backgroundColor: '#fff',
              '&:hover': {
                borderColor: '#5757b0',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
              },
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            }}>
              <TextField
                fullWidth
                value={outputTag}
                onChange={(event) => setOutputTag(event.target.value)}
                placeholder={defaultOutputTag}
                variant="outlined"
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.25 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(event) => handleMenuOpen(event, 'outputTag')}
                startIcon={<AddIcon />}
                sx={{
                  color: '#5757b0',
                  borderColor: '#5757b0',
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#f0f0ff',
                    borderColor: '#5757b0',
                  },
                }}
              >
                {t('workflow.select_tag', 'Select Tag')}
              </Button>
            </Box>
            <Typography sx={{ mt: 1, fontSize: '0.85rem', color: '#666' }}>
              {t('deterministicanswer.output_tag_help', 'The compiled result is saved in this tag.')}
            </Typography>
          </Box>

          <Box sx={{ mb: 0 }}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: '#555' }}>
              {t('deterministicanswer.output_description_label', 'Output Description')}
            </Typography>
            <Box sx={{
              border: '1px solid #d0d0e8',
              borderRadius: '8px',
              backgroundColor: '#fff',
              '&:hover': {
                borderColor: '#5757b0',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.08)',
              },
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            }}>
              <TextField
                fullWidth
                value={outputDescription}
                onChange={(event) => setOutputDescription(event.target.value)}
                multiline
                minRows={2}
                variant="outlined"
              />
            </Box>
            <Typography sx={{ mt: 1, fontSize: '0.85rem', color: '#666' }}>
              {t('deterministicanswer.output_description_help', 'Short description shown in the evidence trail.')}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: freezePlan ? '1px solid rgba(46, 125, 50, 0.22)' : '1px solid #e0e0f010',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          title={(
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.15rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              <Box component="span" sx={{
                bgcolor: freezePlan ? '#2e7d32' : '#64748b',
                color: 'white',
                borderRadius: '6px',
                px: { xs: 1, sm: 1.5 },
                py: { xs: 0.5, sm: 0.7 },
                mr: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                letterSpacing: '0.5px',
                fontWeight: 600,
              }}>
                3
              </Box>
              {t('deterministicanswer.compiled_plan_title', 'Compiled plan')}
              <Chip
                size="small"
                label={
                  freezePlan
                    ? t('deterministicanswer.plan_frozen', 'Frozen')
                    : t('deterministicanswer.plan_dynamic', 'Dynamic')
                }
                color={freezePlan ? 'success' : 'default'}
                variant={freezePlan ? 'filled' : 'outlined'}
              />
            </Typography>
          )}
          sx={{
            borderBottom: '1px solid #f0f0f8',
            backgroundColor: freezePlan ? '#f4fbf5' : '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 0.8fr) 1fr' },
            gap: 2,
            alignItems: 'stretch',
          }}>
            <Box sx={{
              p: 2,
              borderRadius: '10px',
              backgroundColor: hasCompiledPlan ? '#f0fdf4' : '#f8fafc',
              border: hasCompiledPlan ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
            }}>
              <Typography sx={{ fontWeight: 700, color: hasCompiledPlan ? '#166534' : '#475569', mb: 1 }}>
                {hasCompiledPlan
                  ? t('deterministicanswer.compiled_plan_ready', 'Plan ready')
                  : t('deterministicanswer.compiled_plan_not_ready', 'No plan yet')}
              </Typography>
              <Typography sx={{ fontSize: '0.86rem', color: '#64748b' }}>
                {hasCompiledPlan
                  ? t('deterministicanswer.compiled_plan_ready_help', 'This action already has a reusable execution plan.')
                  : t('deterministicanswer.no_compiled_plan_help', 'Run the action once to generate and save a compiled plan.')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                <Chip
                  size="small"
                  label={
                    hasCompiledPlan
                      ? t('deterministicanswer.compiled_plan_saved', '{{count}} step(s) saved', { count: compiledPlanStepCount })
                      : t('deterministicanswer.compiled_plan_missing', 'No compiled plan saved yet')
                  }
                  color={hasCompiledPlan ? 'success' : 'default'}
                  variant={hasCompiledPlan ? 'filled' : 'outlined'}
                />
                {clearCompiledPlanRequested && (
                  <Chip
                    size="small"
                    label={t('deterministicanswer.plan_clear_pending', 'Will clear on save')}
                    color="warning"
                  />
                )}
              </Box>
            </Box>

            <Box sx={{
              p: 2,
              borderRadius: '10px',
              backgroundColor: '#fff',
              border: '1px solid #e8eaf5',
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
              }}>
                <Box>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={freezePlan}
                        onChange={(event) => setFreezePlan(event.target.checked)}
                        color="success"
                      />
                    )}
                    label={t('deterministicanswer.freeze_plan_label', 'Freeze plan')}
                    sx={{ m: 0 }}
                  />
                  <Typography sx={{ mt: 0.5, fontSize: '0.85rem', color: '#666' }}>
                    {t(
                      'deterministicanswer.freeze_plan_help',
                      'Keep the saved compiled plan across runs. Unfreeze to let the next run compile a new plan from the prompt.'
                    )}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant={clearCompiledPlanRequested ? 'contained' : 'outlined'}
                  color={clearCompiledPlanRequested ? 'warning' : 'inherit'}
                  disabled={!hasCompiledPlan && !clearCompiledPlanRequested}
                  onClick={() => setClearCompiledPlanRequested((current) => !current)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    alignSelf: { xs: 'stretch', sm: 'center' },
                  }}
                >
                  {clearCompiledPlanRequested
                    ? t('deterministicanswer.clear_compiled_plan_cancel', 'Keep compiled plan')
                    : t('deterministicanswer.clear_compiled_plan', 'Clear compiled plan')}
                </Button>
              </Box>
              {hasCompiledPlan && (
                <Typography sx={{ mt: 1.5, fontSize: '0.82rem', color: '#666' }}>
                  {t(
                    'deterministicanswer.compiled_plan_summary',
                    {
                      generatedAt: compiledPlanGeneratedAtLabel || t('deterministicanswer.unknown', 'unknown'),
                      sourceMode: compiledPlanSourceMode || t('deterministicanswer.unknown', 'unknown'),
                    }
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card
        sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #e0e0f010',
          overflow: 'hidden',
        }}
      >
        <CardHeader
          title={(
            <Typography sx={{
              fontWeight: 700,
              color: '#444',
              fontSize: { xs: '1rem', sm: '1.15rem' },
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}>
              {t('deterministicanswer.advanced_title', 'Advanced')}
              <Chip
                size="small"
                label={
                  hasCompiledPlan
                    ? t('deterministicanswer.compiled_plan_saved', '{{count}} step(s) saved', { count: compiledPlanStepCount })
                    : t('deterministicanswer.compiled_plan_missing', 'No compiled plan saved yet')
                }
                color={hasCompiledPlan ? 'success' : 'default'}
                variant={hasCompiledPlan ? 'filled' : 'outlined'}
              />
            </Typography>
          )}
          action={(
            <Button
              size="small"
              onClick={() => setAdvancedOpen((current) => !current)}
              endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {advancedOpen
                ? t('deterministicanswer.advanced_hide', 'Hide')
                : t('deterministicanswer.advanced_show', 'Show')}
            </Button>
          )}
          sx={{
            borderBottom: advancedOpen ? '1px solid #f0f0f8' : 'none',
            backgroundColor: '#fafbff',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            p: { xs: 1.5, sm: 2.5 },
          }}
        />
        <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Typography sx={{ fontSize: '0.9rem', color: '#666', mb: 2 }}>
              {t(
                'deterministicanswer.advanced_help',
                'This shows the compiled plan saved on the action. The backend reuses this plan when Freeze plan is enabled.'
              )}
            </Typography>

            {hasCompiledPlan ? (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    size="small"
                    label={t('deterministicanswer.plan_version', 'Version {{version}}', {
                      version: compiledPlan.version || 1,
                    })}
                  />
                  <Chip
                    size="small"
                    label={t('deterministicanswer.plan_mode_label', 'Mode: {{mode}}', {
                      mode: compiledPlanSourceMode || t('deterministicanswer.unknown', 'unknown'),
                    })}
                  />
                  <Chip
                    size="small"
                    label={t('deterministicanswer.plan_generated_label', 'Generated: {{generatedAt}}', {
                      generatedAt: compiledPlanGeneratedAtLabel || t('deterministicanswer.unknown', 'unknown'),
                    })}
                  />
                </Box>

                {compiledPlan.title && (
                  <Typography sx={{ fontWeight: 700, mb: 1, color: '#444' }}>
                    {compiledPlan.title}
                  </Typography>
                )}
                {compiledPlan.reason && (
                  <Typography sx={{ fontSize: '0.9rem', color: '#555', mb: 2 }}>
                    {compiledPlan.reason}
                  </Typography>
                )}

                {(compiledPlan.steps || []).map((step, index) => (
                  <Box
                    key={`${step.id || 'step'}-${index}`}
                    sx={{
                      border: '1px solid #e8eaf5',
                      borderRadius: '10px',
                      p: 2,
                      mb: 1.5,
                      backgroundColor: '#fff',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                      <Chip size="small" label={t('deterministicanswer.plan_step_number', 'Step {{number}}', { number: index + 1 })} />
                      <Chip size="small" label={step.type || t('deterministicanswer.unknown', 'unknown')} color="primary" variant="outlined" />
                      <Chip size="small" label={t('deterministicanswer.plan_step_source', 'Source: {{source}}', { source: step.source || t('deterministicanswer.unknown', 'unknown') })} />
                    </Box>
                    {step.id && (
                      <Typography sx={{ fontWeight: 600, color: '#444', mb: 1 }}>
                        {step.id}
                      </Typography>
                    )}
                    {step.instruction && (
                      <Typography sx={{ fontSize: '0.88rem', color: '#555', mb: 1 }}>
                        {step.instruction}
                      </Typography>
                    )}
                    {step.code && (
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.5,
                          overflowX: 'auto',
                          borderRadius: '8px',
                          backgroundColor: '#111827',
                          color: '#f9fafb',
                          fontSize: '0.8rem',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {step.code}
                      </Box>
                    )}
                    {step.output_schema && (
                      <Box
                        component="pre"
                        sx={{
                          mt: 1,
                          mb: 0,
                          p: 1.5,
                          overflowX: 'auto',
                          borderRadius: '8px',
                          backgroundColor: '#f8f9ff',
                          color: '#374151',
                          fontSize: '0.78rem',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {JSON.stringify(step.output_schema, null, 2)}
                      </Box>
                    )}
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 700, color: '#444', mb: 1 }}>
                  {t('deterministicanswer.raw_plan_title', 'Raw compiled plan')}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    maxHeight: 360,
                    overflow: 'auto',
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    fontSize: '0.78rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {compiledPlanJson}
                </Box>
              </Box>
            ) : (
              <Typography sx={{ color: '#666', fontSize: '0.9rem' }}>
                {t(
                  'deterministicanswer.no_compiled_plan_help',
                  'Run the action once to generate and save a compiled plan.'
                )}
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </Card>

    </ActionSettingsLayout>
  );
};

export default ActionsDeterministicAnswer;
